/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * ------------------------------------------------------------------------------------------------------------
 * ALWAYS OPEN THE MINI-CART AFTER AN ADD-TO-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * The theme only opens the mini-cart when the submitted product form is NOT inside a drawer (see the
 * "openMiniCart" flag in theme.js). Multi-variant products added from a product list therefore add from inside
 * the quick-buy drawer, which shows the in-drawer <cart-notification> instead. The following change forces the mini-cart open on
 * every successful add and closes the quick-buy drawer it came from.
 */
(function () {
  let addPending = false;

  document.addEventListener('variant:added', function (event) {
    addPending = true;

    // Close the quick-buy drawer the add came from, so its in-drawer
    // cart-notification is suppressed and the mini-cart isn't stacked behind it.
    const quickBuy = event.target.closest('.drawer--quick-buy');

    if (quickBuy) {
      quickBuy.open = false;
    }
  }, true);

  document.addEventListener('cart:refresh', function (event) {
    if (!addPending) {
      return;
    }

    addPending = false;

    const miniCart = document.getElementById('mini-cart');

    // Already open: let it re-render in place instead of replaying the opening animation
    if (!miniCart || miniCart.open) {
      return;
    }

    if (event.detail && event.detail.cart) {
      event.detail.openMiniCart = true;
    }
  }, true);
})();

/**
 * ------------------------------------------------------------------------------------------------------------
 * PRODUCT DESCRIPTION READ MORE / READ LESS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Long descriptions render truncated by default (see the "description" block in product-form.liquid), with
 * these two links swapping in the full text.
 */
(function () {
  var readMore = document.getElementById('read-more');
  var readLess = document.getElementById('read-less');
  var truncatedDescription = document.getElementById('truncated-product-description');
  var fullDescription = document.getElementById('product-description');

  function showHideDescription() {
    if (this.id === 'read-more') {
      truncatedDescription.style.display = 'none';
      fullDescription.style.display = 'block';
    } else {
      truncatedDescription.style.display = 'block';
      fullDescription.style.display = 'none';
    }
  }

  if (readMore) {
    readMore.addEventListener('click', showHideDescription);
  }

  if (readLess) {
    readLess.addEventListener('click', showHideDescription);
  }
})();

/**
 * ------------------------------------------------------------------------------------------------------------
 * VARIANT-DEPENDENT CONTENT OUTSIDE THE PARTIAL RE-RENDER ALLOWLIST
 * ------------------------------------------------------------------------------------------------------------
 *
 * Focal 13's <product-rerender allow-partial-rerender> only swaps sub-elements whose data-block-type is one of
 * a fixed set (meta, payment-terms, variant-picker, volume-pricing, quantity-selector, inventory, buy-buttons,
 * pickup-availability, liquid). Price, compare-at, discount labels and unit pricing all live inside the native
 * "meta" block, so they refresh automatically on a variant change and need nothing here.
 *
 * The three items below don't live inside an allowlisted block (the Taste Guarantee badge is in product-media,
 * which carries no data-block-type at all; the Taste Guarantee notice and the best-before-date row are inside
 * custom "description"/"selling_points" blocks, which aren't on the allowlist). Every variant's markup is
 * pre-rendered with the non-selected ones hidden, and this just keeps the visible one in sync.
 */
document.addEventListener('variant:changed', function (event) {
  var variant = event.detail.variant;

  function showOnlyMatching(className, expectedId) {
    var elements = document.getElementsByClassName(className);

    for (var i = 0; i < elements.length; i++) {
      elements[i].style.display = elements[i].id === expectedId ? 'block' : 'none';
    }
  }

  showOnlyMatching('product-bbd', variant.id + '-bbd');
  showOnlyMatching('taste-guarantee-badge', variant.id + '-taste-guarantee-badge');
  showOnlyMatching('taste-guarantee-notice', variant.id + '-taste-guarantee-notice');
});

/**
 * ------------------------------------------------------------------------------------------------------------
 * KLAVIYO LEGACY BACK IN STOCK: KEEP "TILAA ILMOITUS" WORKING AFTER A VARIANT SWITCH
 * ------------------------------------------------------------------------------------------------------------
 *
 * The legacy Klaviyo snippet (layout/theme.liquid) only places its button on page load. Focal re-renders the
 * buy-buttons block on every variant change, which removes it, so switching to a sold-out variant showed no
 * button at all. This puts it back on the main product form; Klaviyo handles the click through a listener on
 * <body>. Quick-buy forms are skipped, as the snippet only knows about the page's own product.
 *
 * Klaviyo can't be tested on 127.0.0.1:9292: it fetches the product JSON from the hostname without the port.
 * Use the theme preview URL (?preview_theme_id=...) instead.
 */
(function () {
  var BIS_CLASS = 'product-form__add-button button button--primary button--full klaviyo-oos klaviyo-bis-trigger';
  var BIS_TEXT = 'Tilaa ilmoitus';
  var BIS_STYLE = 'text-align: center; margin: 0px; width: auto;';
  var scheduled = false;

  function sync() {
    scheduled = false;

    var container = document.getElementById('MainPaymentContainer');
    var atc = container && container.querySelector('#AddToCart');

    if (!atc) {
      return;
    }

    var soldOut = atc.disabled && atc.classList.contains('button--ternary');
    var triggers = container.querySelectorAll('.klaviyo-bis-trigger');

    if (soldOut) {
      if (triggers.length === 0) {
        var link = document.createElement('a');
        link.href = '#';
        link.className = BIS_CLASS;
        link.setAttribute('style', BIS_STYLE);
        link.textContent = BIS_TEXT;
        atc.parentNode.insertBefore(link, atc);
      } else {
        triggers.forEach(function (trigger) {
          trigger.style.display = '';
        });
      }

      atc.style.display = 'none';
    } else {
      triggers.forEach(function (trigger) {
        trigger.style.display = 'none';
      });

      atc.style.display = '';
    }

    // Klaviyo picks the modal's variant on "change", reading ?variant= first. Focal fires that change
    // before it updates the URL, so Klaviyo lags one variant behind. Re-fire it now the URL is current.
    document.body.dispatchEvent(new Event('change'));
  }

  document.addEventListener('variant:changed', function (event) {
    // The snippet is left out of some product templates (e.g. product.poistunut)
    if (!document.querySelector('script[src*="klaviyo.com/media/js/onsite/onsite.js"]')) {
      return;
    }

    if (!event.target.querySelector || !event.target.querySelector('#MainPaymentContainer')) {
      return;
    }

    if (!scheduled) {
      scheduled = true;
      requestAnimationFrame(sync);
    }
  });
})();

/**
 * ------------------------------------------------------------------------------------------------------------
 * CUSTOM SECTION ELEMENTS: <loyalty-order-list> AND <link-grid>
 * ------------------------------------------------------------------------------------------------------------
 *
 * These two elements back the custom loyalty-overview and link-grid sections. They previously lived inside
 * theme.js, which is Focal's own bundle and is replaced wholesale on every theme update - so they were lost
 * on the 8 -> 13 upgrade. They are defined here instead, where theme updates cannot clobber them.
 *
 * Focal's own <prev-next-buttons> still emits the "prev-next:prev"/"prev-next:next" events these rely on, and
 * window.themeVariables is still exposed. Focal's internal base class is module-scoped and unreachable from
 * here, so both extend HTMLElement directly and use platform APIs.
 */
(function () {
  class LoyaltyOrderList extends HTMLElement {
    connectedCallback() {
      if (!this.hasAttribute('scrollable')) {
        return;
      }

      this.scroller = this.querySelector('.loyalty-list__scroller');

      if (!this.scroller) {
        return;
      }

      this.addEventListener('prev-next:prev', this._scrollByItem.bind(this, -1));
      this.addEventListener('prev-next:next', this._scrollByItem.bind(this, 1));
    }

    _scrollByItem(sign) {
      const item = this.querySelector('.loyalty-list__item');

      if (!item) {
        return;
      }

      const directionFlip = window.themeVariables?.settings?.direction === 'rtl' ? -1 : 1;

      this.scroller.scrollBy({
        left: sign * item.clientWidth * directionFlip,
        behavior: 'smooth',
      });
    }
  }

  if (!customElements.get('loyalty-order-list')) {
    customElements.define('loyalty-order-list', LoyaltyOrderList);
  }

  class LinkGrid extends HTMLElement {
    connectedCallback() {
      if (!this.hasAttribute('reveal-on-scroll')) {
        return;
      }

      const items = Array.from(this.querySelectorAll('.link-grid__item'));

      if (items.length === 0) {
        return;
      }

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const observer = new IntersectionObserver(
        (entries, self) => {
          if (!entries[0].isIntersecting) {
            return;
          }

          self.disconnect();

          items.forEach((item, index) => {
            // "backwards" fill keeps a staggered item hidden during its delay, rather than
            // showing it and then flashing back to transparent when its animation starts.
            item.animate(
              [
                { opacity: 0, transform: reducedMotion ? 'translateY(0)' : 'translateY(30px)' },
                { opacity: 1, transform: 'translateY(0)' },
              ],
              {
                duration: 300,
                delay: reducedMotion ? 0 : 100 * index,
                easing: 'ease',
                fill: 'backwards',
              }
            );
          });
        },
        { rootMargin: '50px 0px', threshold: 0 }
      );

      observer.observe(this);
    }
  }

  if (!customElements.get('link-grid')) {
    customElements.define('link-grid', LinkGrid);
  }
})();
